import { invariant } from '@epic-web/invariant';
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { PMSReportType } from '@src/containers/Portfolio/components/Reports/getPortfolioReportOptions';
import type { SavedReport } from '@src/containers/Portfolio/components/types';
import type { AppStateListenerStart } from '@src/providers/AppStateProvider/types';
import { LookbackOption, type DateRangeOrLookback } from '@talos/kyoko';
import type { SerializedDockview } from 'dockview';
import type { WritableDraft } from 'immer';
import { omitBy } from 'lodash-es';
import type { OpsBalancesChartDimension } from '../../OperationsOverview/BalancesChart/types';
import type { OperationsOverviewShowBy, OpsOverviewFilter } from '../../OperationsOverview/types';
import type { OperationsOverviewShowByOld } from '../../OperationsOverviewOld/types';
import type {
  PortfolioOverviewChartDisplayByType,
  PortfolioOverviewChartFieldOptionValue,
} from '../components/PortfolioOverview/PortfolioOverviewChart/PortfolioOverviewChartTypes';
import {
  getOperationsLayoutOptions,
  getPortfolioLayoutOptions,
} from '../layoutConfiguration/getPortfolioLayoutOptions';
import type { PortfolioViewType } from '../portfolioDashboardLayouts';
import type { PortfolioManagementPositionFilter, PortfolioViewState, RiskAggMode, RiskPivotType } from '../types/types';
import { migrateUserLayoutMapKeys } from './migrateUserLayoutMapKeys';

export interface PortfolioViewLayoutState {
  /** Storage mapping for DockView storage.
   * It's a bit tough to more strongly type the key as the key is dependent on the workspace and tab type */
  userLayoutMapForViewTab: { [key: string]: SerializedDockview };
  viewState: PortfolioViewState;
}

interface PortfolioViewLayoutActionPayload {
  view: PortfolioViewType;
  tabLabel: string;
  userLayout: SerializedDockview;
}

/** v7: rename PMS Full flexible-layout keys after Portfolio/Operations route restructure */
const USER_LAYOUT_STORAGE_KEY_VERSION_CURRENT = 7;
const USER_LAYOUT_STORAGE_KEY_VERSIONS_TO_READ = [7, 6, 5] as const;
const getUserLayoutStorageKey = (version: number) => `portfolioview.DockViewUserLayout-${version}`;
const getUserLayoutStorage = (): PortfolioViewLayoutState['userLayoutMapForViewTab'] => {
  for (const version of USER_LAYOUT_STORAGE_KEY_VERSIONS_TO_READ) {
    const stored = localStorage.getItem(getUserLayoutStorageKey(version));
    if (!stored) {
      continue;
    }

    const parsed = JSON.parse(stored) as PortfolioViewLayoutState['userLayoutMapForViewTab'];
    const migrated = migrateUserLayoutMapKeys(parsed);

    if (version !== USER_LAYOUT_STORAGE_KEY_VERSION_CURRENT || migrated !== parsed) {
      localStorage.setItem(getUserLayoutStorageKey(USER_LAYOUT_STORAGE_KEY_VERSION_CURRENT), JSON.stringify(migrated));
    }

    return migrated;
  }

  return {};
};
const userLayoutStorage = {
  currentValue: getUserLayoutStorage(),
  saveToStorage: (dockViewToStore: PortfolioViewLayoutState['userLayoutMapForViewTab']) => {
    localStorage.setItem(
      getUserLayoutStorageKey(USER_LAYOUT_STORAGE_KEY_VERSION_CURRENT),
      JSON.stringify(dockViewToStore)
    );
  },
};

/** Create the initial portfolio view state based on prior state
 * - if any input prop values are undefined, the default overrides it
 */
export function getInitPortfolioViewState(
  viewState: Partial<PortfolioViewLayoutState['viewState']>
): PortfolioViewLayoutState['viewState'] {
  return {
    selectedPortfolioId: undefined,
    selectedMarketAccountIds: undefined,
    showRollupHierarchy: false,
    showZeroBalances: false,
    dateRange: {
      lookback: LookbackOption.Past24Hours,
    },
    includeCash: false,
    overviewChartDisplayType: 'byInstrument',
    overviewChartFieldType: 'position',
    overviewTopPositionsFilterCount: 0,
    riskPivotAggMode: 'Net',
    riskPivotType: 'Tenor',
    opsOverviewShowByOld: 'MarketAccount',
    opsOverviewFilterOld: {},
    opsOverviewShowBy: 'MarketAccount',
    opsOverviewFilter: {},
    opsOverviewBalancesChartDimension: 'balances',
    opsOverviewBalancesChartShowByAsset: false,
    pmMarginShowBy: 'MarketAccount',
    pmMarginFilter: {},
    pmMarginBalancesChartDimension: 'balances',
    pmMarginBalancesChartShowByAsset: false,
    pmOverviewFilter: {},
    pmPerformanceFilter: {},
    pmRiskFilter: {},
    performanceTopPositionsFilterCount: 0,
    reports: {
      'PM-performance-report': [],
      'OPS-treasury-report': [],
    },
    ...omitBy(viewState, value => value === undefined),
  };
}
const initialState: PortfolioViewLayoutState = {
  viewState: getInitPortfolioViewState({}),
  userLayoutMapForViewTab: userLayoutStorage.currentValue,
};

export function getKeyForUserLayout(view: PortfolioViewType, tabLabel: string): string {
  return `${view}::${tabLabel}`;
}

export function getLayoutForViewTab(
  userLayoutMap: PortfolioViewLayoutState['userLayoutMapForViewTab'],
  view: PortfolioViewType,
  tabLabel: string
): SerializedDockview {
  return userLayoutMap[getKeyForUserLayout(view, tabLabel)];
}

export function getDefaultLayoutForViewTab(view: PortfolioViewType, tabLabel: string): SerializedDockview {
  const layoutOptionTab = [...getPortfolioLayoutOptions(), ...getOperationsLayoutOptions()]
    .find(option => option.value === view)
    ?.tabs.filter(tab => tab.layoutType === 'flexible')
    .find(tab => tab.label === tabLabel);
  invariant(layoutOptionTab, `Could not find layout option for view ${view} and tab ${tabLabel}`);
  return layoutOptionTab.getDefaultLayout();
}

const portfolioViewLayoutSlice = createSlice({
  name: 'portfolioViewLayout',
  initialState,
  reducers: {
    setPortfolioViewState(state, action: PayloadAction<PortfolioViewLayoutState['viewState']>) {
      state.viewState = action.payload;
    },
    changeSelectedPortfolioId: (
      state,
      action: PayloadAction<{
        selectedPortfolioId: number;
        onlyIfNotSet?: boolean;
      }>
    ) => {
      if (action.payload.onlyIfNotSet && state.viewState.selectedPortfolioId !== undefined) {
        return;
      }
      state.viewState.selectedPortfolioId = action.payload.selectedPortfolioId;
    },
    updateDateRange: (state, action: PayloadAction<DateRangeOrLookback>) => {
      state.viewState.dateRange = action.payload;
    },
    changeMarketAccountIds: (state, action: PayloadAction<string[] | undefined>) => {
      state.viewState.selectedMarketAccountIds = action.payload;
    },
    updateLayout: (
      state: WritableDraft<PortfolioViewLayoutState>,
      action: { payload: PortfolioViewLayoutActionPayload; type: string }
    ) => {
      state.userLayoutMapForViewTab[getKeyForUserLayout(action.payload.view, action.payload.tabLabel)] =
        action.payload.userLayout;
    },
    updateShowRollupHierarchy(state, action: PayloadAction<boolean>) {
      state.viewState.showRollupHierarchy = action.payload;
    },
    updateShowZeroBalances(state, action: PayloadAction<boolean>) {
      state.viewState.showZeroBalances = action.payload;
    },
    updateIncludeCash(state, action: PayloadAction<boolean>) {
      state.viewState.includeCash = action.payload;
    },
    updateOverviewChartDisplayType(state, action: PayloadAction<PortfolioOverviewChartDisplayByType>) {
      state.viewState.overviewChartDisplayType = action.payload;
    },
    updateOverviewChartFieldType(state, action: PayloadAction<PortfolioOverviewChartFieldOptionValue>) {
      state.viewState.overviewChartFieldType = action.payload;
    },
    updateOverviewTopPositionsFilterCount(state, action: PayloadAction<number>) {
      state.viewState.overviewTopPositionsFilterCount = action.payload;
    },
    updateRiskPivotType(state, action: PayloadAction<RiskPivotType>) {
      state.viewState.riskPivotType = action.payload;
    },
    updateRiskPivotAggMode(state, action: PayloadAction<RiskAggMode>) {
      state.viewState.riskPivotAggMode = action.payload;
    },
    updateOpsOverviewShowByOld(state, action: PayloadAction<OperationsOverviewShowByOld>) {
      state.viewState.opsOverviewShowByOld = action.payload;
    },
    updateOpsOverviewFilterOld(state, action: PayloadAction<OpsOverviewFilter>) {
      state.viewState.opsOverviewFilterOld = action.payload;
    },
    updateOpsOverviewShowBy(state, action: PayloadAction<OperationsOverviewShowBy>) {
      state.viewState.opsOverviewShowBy = action.payload;
    },
    updateOpsOverviewFilter(state, action: PayloadAction<OpsOverviewFilter>) {
      state.viewState.opsOverviewFilter = action.payload;
    },
    updateOpsOverviewBalancesChartDimension(state, action: PayloadAction<OpsBalancesChartDimension>) {
      state.viewState.opsOverviewBalancesChartDimension = action.payload;
    },
    updateOpsOverviewBalancesChartShowByAsset(state, action: PayloadAction<boolean>) {
      state.viewState.opsOverviewBalancesChartShowByAsset = action.payload;
    },
    updatePMMarginShowBy(state, action: PayloadAction<OperationsOverviewShowBy>) {
      state.viewState.pmMarginShowBy = action.payload;
    },
    updatePMMarginFilter(state, action: PayloadAction<OpsOverviewFilter>) {
      state.viewState.pmMarginFilter = action.payload;
    },
    updatePMMarginBalancesChartDimension(state, action: PayloadAction<OpsBalancesChartDimension>) {
      state.viewState.pmMarginBalancesChartDimension = action.payload;
    },
    updatePMMarginBalancesChartShowByAsset(state, action: PayloadAction<boolean>) {
      state.viewState.pmMarginBalancesChartShowByAsset = action.payload;
    },
    updatePMOverviewFilter(state, action: PayloadAction<PortfolioManagementPositionFilter>) {
      state.viewState.pmOverviewFilter = action.payload;
    },
    updatePMPerformanceFilter(state, action: PayloadAction<PortfolioManagementPositionFilter>) {
      state.viewState.pmPerformanceFilter = action.payload;
    },
    updatePMRiskFilter(state, action: PayloadAction<PortfolioManagementPositionFilter>) {
      state.viewState.pmRiskFilter = action.payload;
    },
    updatePerformanceTopPositionsFilterCount(state, action: PayloadAction<number>) {
      state.viewState.performanceTopPositionsFilterCount = action.payload;
    },
    updateReports(state, action: PayloadAction<Partial<Record<PMSReportType, SavedReport[]>>>) {
      state.viewState.reports = action.payload;
    },
    updateInlineDetailBreadcrumbLabel(state, action: PayloadAction<string | undefined>) {
      if (action.payload == null) {
        delete state.viewState.inlineDetailBreadcrumbLabel;
        return;
      }
      state.viewState.inlineDetailBreadcrumbLabel = action.payload;
    },
  },
});

export function setupListeners(startListening: AppStateListenerStart) {
  const unsubscribe = startListening({
    predicate: action => action.type === portfolioViewLayoutSlice.actions.updateLayout.type,
    effect: async (_action, listenerApi) => {
      const newState = listenerApi.getState();
      userLayoutStorage.saveToStorage(newState.portfolioViewLayout.userLayoutMapForViewTab);
    },
  });

  return () => {
    unsubscribe({ cancelActive: true });
  };
}

export const portfolioViewLayoutSliceActions = { ...portfolioViewLayoutSlice.actions };
export const portfolioViewLayoutSliceReducer = portfolioViewLayoutSlice.reducer;
